import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOnboarding, useOnboardingCheckout } from '../../hooks/useOnboarding';
import { CreditCard, Loader2, Check, ShieldCheck, AlertCircle, ArrowRight, Home } from 'lucide-react';
import { trackConversion } from '../../lib/analytics';

const BASE_PRICE = 49;
const EXTRA_PRICE = 15;
const YEARLY_MONTHS = 11;
const MAX_UNITS = 50;
const monthlyFor = (units: number) => BASE_PRICE + EXTRA_PRICE * (Math.max(1, units) - 1);

function storedPlan(): { units?: number; billing?: 'monthly' | 'yearly' } {
  try { return JSON.parse(localStorage.getItem('kx_plan') || '{}'); } catch { return {}; }
}

export default function PaymentPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useOnboarding();
  const checkout = useOnboardingCheckout();
  const [params] = useSearchParams();
  const canceled = params.get('canceled') === '1';

  const [units, setUnits] = useState(1);
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const [hydrated, setHydrated] = useState(false);

  // Seed from the plan carried over from the landing page, then from the server.
  useEffect(() => {
    if (hydrated || !data) return;
    const plan = storedPlan();
    setUnits(data.payment.units || plan.units || data.profile.propertyCount || 1);
    setBilling(data.payment.billing || plan.billing || 'monthly');
    setHydrated(true);
  }, [data, hydrated]);

  if (isLoading || !data) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={32} /></div>;
  }

  const signed = data.agreement.signed;
  const paid = data.payment.status === 'paid';
  const monthly = monthlyFor(units);
  const displayTotal = billing === 'yearly' ? monthly * YEARLY_MONTHS : monthly;

  const pay = async () => {
    trackConversion('app_checkout_start', { units, billing, value: displayTotal, currency: 'EUR' }, 'InitiateCheckout');
    const res = await checkout.mutateAsync({ units, billing, siteModel: data.payment.siteModel });
    if (res.url) window.location.href = res.url;
  };

  // DEV/TEST: mark onboarding paid without Stripe (needs the build flag + the
  // PAYMENT_TEST_BYPASS_KEY secret on the server). Never shown in normal builds.
  const skipTest = async () => {
    const key = window.prompt('Clave de bypass de pago (PAYMENT_TEST_BYPASS_KEY):');
    if (!key) return;
    const res = await fetch('/api/portal/onboarding/test-pay', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key, units, billing }),
      credentials: 'same-origin',
    });
    if (res.ok) window.location.reload();
    else window.alert('Bypass no disponible (revisa VITE_ENABLE_TEST_BYPASS y PAYMENT_TEST_BYPASS_KEY).');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
          <CreditCard size={20} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('portal.payment.title')}</h1>
          <p className="text-slate-500 mt-0.5">{t('portal.payment.subtitle')}</p>
        </div>
      </div>

      {paid ? (
        <div className="space-y-5">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800">
            <Check size={22} className="shrink-0" />
            <div className="text-sm">
              <p className="font-semibold">{t('portal.payment.paidTitle')}</p>
              <p className="text-emerald-700">{t('portal.payment.paidDesc')}</p>
            </div>
          </div>
          <Link to="/portal/onboarding"
            className="flex items-center justify-center gap-2 w-full py-3.5 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors">
            {t('portal.payment.continue')} <ArrowRight size={18} />
          </Link>
        </div>
      ) : !signed ? (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          <AlertCircle size={20} className="shrink-0" />
          <span>{t('portal.payment.signFirst')}{' '}
            <Link to="/portal/agreement" className="font-semibold underline">{t('portal.payment.goSign')}</Link>
          </span>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
          {canceled && (
            <div className="p-3 rounded-lg bg-amber-50 text-amber-700 text-sm border border-amber-200">
              {t('portal.payment.canceled')}
            </div>
          )}

          {/* Units */}
          <div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Home size={16} className="text-teal-600" /> {t('portal.payment.unitsLabel')}
              </label>
              <span className="rounded-full bg-teal-50 px-3 py-1 text-base font-extrabold text-teal-700 tabular-nums ring-1 ring-teal-200">{units}</span>
            </div>
            <input
              type="range" min={1} max={MAX_UNITS} step={1} value={units}
              onChange={e => setUnits(Number(e.target.value))}
              className="mt-4 w-full accent-teal-600"
            />
            <div className="mt-1 flex justify-between text-xs text-slate-400"><span>1</span><span>{MAX_UNITS}</span></div>
          </div>

          {/* Billing toggle */}
          <div className="mx-auto flex w-fit items-center rounded-full bg-slate-100 p-1 text-sm font-semibold">
            <button onClick={() => setBilling('monthly')}
              className={`rounded-full px-4 py-1.5 transition-colors ${billing === 'monthly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
              {t('portal.payment.monthly')}
            </button>
            <button onClick={() => setBilling('yearly')}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 transition-colors ${billing === 'yearly' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'}`}>
              {t('portal.payment.yearly')}
              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">{t('portal.payment.yearlyBadge')}</span>
            </button>
          </div>

          {/* Price */}
          <div className="text-center">
            <div className="flex items-end justify-center gap-1">
              <span className="text-5xl font-extrabold text-slate-900 tabular-nums">€{displayTotal}</span>
              <span className="mb-1 text-base text-slate-400">{billing === 'yearly' ? t('portal.payment.perYear') : t('portal.payment.perMonth')}</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">
              {billing === 'yearly'
                ? <>≈ €{monthly}{t('portal.payment.perMonth')} · <span className="font-semibold text-emerald-600">{t('portal.payment.oneMonthFree')}</span></>
                : <><span className="font-semibold text-slate-700">€{BASE_PRICE}</span> {t('portal.payment.priceFirst')}{units > 1 && <> + <span className="font-semibold text-slate-700">€{EXTRA_PRICE}</span> {t('portal.payment.priceExtra', { count: units - 1 })}</>}</>}
            </p>
          </div>

          {checkout.isError && <p className="text-sm text-rose-600 text-center">{t('portal.payment.error')}</p>}

          <button onClick={pay} disabled={checkout.isPending}
            className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 text-white font-semibold rounded-xl transition-colors">
            {checkout.isPending ? <Loader2 size={18} className="animate-spin" /> : <ShieldCheck size={18} />}
            {t('portal.payment.pay')}
          </button>
          <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5">
            <ShieldCheck size={13} /> {t('portal.payment.guaranteeNote')}
          </p>
          {import.meta.env.VITE_ENABLE_TEST_BYPASS === '1' && (
            <button onClick={skipTest} className="w-full mt-1 text-xs text-amber-500 hover:text-amber-600 underline">
              [TEST] Marcar como pagado sin Stripe
            </button>
          )}
        </div>
      )}
    </div>
  );
}
